/**
 * web/screens/gameEnding.ts
 * Ending-cutscene wiring extracted from {@link gameScreen} so that
 * `web/screens/game.ts` stays under the 300-line cap mandated by
 * AGENTS.md — the same reason `gameIntro.ts` exists.
 *
 * The ending fires when the Star-Eater arena (`5-B`) is cleared. Unlike
 * the intro it is **not** gated by a save flag: beating the final boss
 * again should roll the ending again, and gating it would mean the one
 * time the player most expects a payoff (a replay clear) silently drops
 * them back on the world map.
 *
 * @module web/screens/gameEnding
 */

/// <reference lib="dom" />

import { showEndingCutscene, type EndingPanel } from '../endingOverlay';
import type { OverlayHandle } from '../overlays';
import { getEndingCutscene } from '@src/game/services/narrativeManager';

/**
 * Closing credits panel.
 *
 * DECISION: this deliberately names no contributors. Inventing a credits
 * roll would put unverifiable names in shipped copy; the real roll is a
 * content task for the project owner. The panel still gives the sequence
 * a deliberate final beat instead of ending mid-narration.
 */
const CREDITS_TEXT = 'STARFALL COURIER\n\nThank you for playing.';

/**
 * Build the ordered panel list for the ending sequence.
 *
 * Exported for testing so the panel order and speaker formatting can be
 * asserted without mounting DOM.
 *
 * @returns Narrative lines, then the closing beat, then credits.
 */
export function buildEndingPanels(): EndingPanel[] {
  const { lines, closingText } = getEndingCutscene();
  const panels: EndingPanel[] = lines.map((l) => ({
    text: l.speaker ? `${l.speaker}: ${l.text}` : l.text,
    variant: 'line',
  }));
  if (closingText.trim().length > 0) {
    panels.push({ text: closingText, variant: 'closing' });
  }
  panels.push({ text: CREDITS_TEXT, variant: 'credits' });
  return panels;
}

/**
 * Mount the ending cutscene over `root`.
 *
 * @param root   The game-screen root element the overlay attaches to.
 * @param onDone Invoked once the player dismisses the final panel —
 *               the caller navigates away from here.
 * @returns The overlay handle, so the host screen can tear the ending
 *          down if the route is destroyed mid-sequence.
 */
export function startEndingCutscene(root: HTMLElement, onDone: () => void): OverlayHandle {
  return showEndingCutscene(root, buildEndingPanels(), onDone);
}
